import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TrendingUp, Shield, Zap, Lock, Users } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1642432556591-72cbc671b707?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          maskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)'
        }}
      />
      
      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `
          linear-gradient(to right, #1F1F1F 1px, transparent 1px),
          linear-gradient(to bottom, #1F1F1F 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px'
      }} />

      {/* Header */}
      <header className="relative z-10 border-b border-[#1F1F1F] bg-black/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#00FF94] rounded flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              CoinPulse
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Private Badge */}
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#6366F1]/30 bg-[#6366F1]/10 text-[#6366F1] text-xs font-mono uppercase tracking-wider">
              <Lock className="w-3 h-3" />
              Private
            </span>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold"
              data-testid="header-login-btn"
            >
              Member Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-32">
          <div className="max-w-3xl">
            {/* Private Invite Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#6366F1]/30 bg-[#6366F1]/10 mb-8">
              <Lock className="w-4 h-4 text-[#6366F1]" />
              <span className="text-sm font-medium text-[#6366F1]">
                Private Invite Only
              </span>
            </div>

            {/* Headline */}
            <h1 
              className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white mb-6 leading-[1.1]"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Exclusive Trading<br />
              <span className="text-[#00FF94]">Signals Club.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-[#A1A1AA] mb-10 leading-relaxed max-w-2xl">
              CoinPulse is a private community of elite traders. Access is by invitation only. 
              If you've received an invite, use your credentials to log in.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/login')}
                className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold text-base h-12 px-8"
                data-testid="hero-login-btn"
              >
                <Lock className="mr-2 w-4 h-4" />
                Member Login
              </Button>
            </div>

            {/* Private Info */}
            <div className="flex items-center gap-3 mt-8 p-4 rounded-lg border border-[#1F1F1F] bg-[#0A0A0A]/50">
              <div className="w-10 h-10 rounded-full bg-[#6366F1]/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-[#6366F1]" />
              </div>
              <div>
                <p className="text-sm text-white font-medium">Invitation Required</p>
                <p className="text-xs text-[#737373]">
                  New members are added by existing members only. No public registration.
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mt-16 pt-8 border-t border-[#1F1F1F]">
              <div>
                <p className="text-3xl font-bold text-white font-mono">500+</p>
                <p className="text-sm text-[#737373] mt-1">Signals Delivered</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white font-mono">78%</p>
                <p className="text-sm text-[#737373] mt-1">Win Rate</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white font-mono">Elite</p>
                <p className="text-sm text-[#737373] mt-1">Members Only</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="border-t border-[#1F1F1F] bg-[#0A0A0A]/50">
          <div className="max-w-7xl mx-auto px-6 py-24">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Why Members Choose Us
              </h2>
              <p className="text-[#A1A1AA] max-w-xl mx-auto">
                Exclusive access to premium trading signals and a community of serious traders.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Feature 1 */}
              <div className="p-6 border border-[#1F1F1F] rounded-lg bg-[#0A0A0A] hover:border-[#00FF94]/30 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 flex items-center justify-center mb-5">
                  <Zap className="w-6 h-6 text-[#00FF94]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Premium Signals</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  Carefully curated trading setups with detailed entry, stop loss, and take profit levels.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="p-6 border border-[#1F1F1F] rounded-lg bg-[#0A0A0A] hover:border-[#00FF94]/30 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 flex items-center justify-center mb-5">
                  <TrendingUp className="w-6 h-6 text-[#00FF94]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Expert Analysis</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  Every signal backed by thorough technical and fundamental analysis from experienced traders.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="p-6 border border-[#1F1F1F] rounded-lg bg-[#0A0A0A] hover:border-[#6366F1]/30 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#6366F1]/10 flex items-center justify-center mb-5">
                  <Shield className="w-6 h-6 text-[#6366F1]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Private Community</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  Invite-only membership ensures quality discussions and serious traders only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1F1F1F] bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#00FF94] rounded flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-sm text-white">CoinPulse</span>
            <span className="text-xs text-[#6366F1] ml-2 px-2 py-0.5 rounded border border-[#6366F1]/30 bg-[#6366F1]/10">
              Private
            </span>
          </div>
          <p className="text-xs text-[#737373]">
            © 2025 CoinPulse. Invite-only trading signals.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
